import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ImageUpload } from "@/components/ImageUpload";
import { LocationCurrency } from "@/components/admin/LocationCurrency";
import { OpeningHoursEditor } from "@/components/admin/OpeningHoursEditor";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";

const COLORS = ["#D9531E", "#F97316", "#059669", "#2563EB", "#7C3AED", "#DB2777", "#DC2626", "#0D9488"];

export const BrandingEditor = ({ restaurant, onUpdate }) => {
  const [form, setForm] = useState({
    name: restaurant.name || "",
    description: restaurant.description || "",
    logo_path: restaurant.logo_path || null,
    cover_path: restaurant.cover_path || null,
    payment_qr_path: restaurant.payment_qr_path || null,
    payment_qr_label: restaurant.payment_qr_label || "",
    primary_color: restaurant.primary_color || "#D9531E",
    table_count: restaurant.table_count || 8,
    address: restaurant.address || "",
    country: restaurant.country || "",
    currency_symbol: restaurant.currency_symbol || "$",
    timezone: restaurant.timezone || null,
    opening_hours: restaurant.opening_hours || null,
  });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/my/restaurant", { ...form, table_count: parseInt(form.table_count) || 1 });
      onUpdate(data);
      toast.success("Branding updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save branding");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <h2 className="font-heading text-2xl font-bold mb-1">Brand & settings</h2>
      <p className="text-slate-400 text-sm mb-6">Customise how your menu looks to guests.</p>
      <form onSubmit={save} className="max-w-xl space-y-6 bg-slate-900 border border-slate-800 rounded-2xl p-6">
        <div className="flex flex-wrap gap-8">
          <ImageUpload value={form.logo_path} onChange={(p) => setForm({ ...form, logo_path: p })} label="Logo" testid="branding-logo-upload" />
          <ImageUpload value={form.cover_path} onChange={(p) => setForm({ ...form, cover_path: p })} label="Cover image" testid="branding-cover-upload" aspect="wide" />
        </div>
        <div className="border-t border-slate-800 pt-6">
          <p className="text-sm font-semibold">Payment QR</p>
          <p className="text-xs text-slate-400 mb-3">Upload your PayNow, DuitNow or other payment QR. Diners see it under their order total.</p>
          <div className="flex flex-wrap items-end gap-6">
            <ImageUpload value={form.payment_qr_path} onChange={(p) => setForm({ ...form, payment_qr_path: p || "" })} label="QR image" testid="branding-payment-qr-upload" />
            <div className="flex-1 min-w-[180px]">
              <label className="text-xs font-medium uppercase tracking-wider text-slate-400">Payment method name</label>
              <input data-testid="branding-payment-qr-label" maxLength={40} value={form.payment_qr_label} onChange={(e) => setForm({ ...form, payment_qr_label: e.target.value })} placeholder="e.g. PayNow" className="mt-1.5 w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-orange-500" />
            </div>
          </div>
        </div>
        <LocationCurrency form={form} setForm={setForm} />
        <OpeningHoursEditor form={form} setForm={setForm} />
        <div>
          <label className="text-xs font-medium uppercase tracking-wider text-slate-400">Restaurant name</label>
          <input data-testid="branding-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-orange-500" />
        </div>
        <div>
          <label className="text-xs font-medium uppercase tracking-wider text-slate-400">Tagline / description</label>
          <input data-testid="branding-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5 w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-orange-500" />
        </div>
        <div>
          <label className="text-xs font-medium uppercase tracking-wider text-slate-400">Accent colour</label>
          <div className="flex flex-wrap gap-2 mt-2">
            {COLORS.map((c) => (
              <button type="button" key={c} data-testid={`color-${c}`} onClick={() => setForm({ ...form, primary_color: c })} style={{ backgroundColor: c }} className={`h-9 w-9 rounded-full border-2 transition-transform hover:scale-110 ${form.primary_color === c ? "border-white scale-110" : "border-transparent"}`} />
            ))}
          </div>
        </div>
        <div className="w-40">
          <label className="text-xs font-medium uppercase tracking-wider text-slate-400">Number of tables</label>
          <input data-testid="branding-tables-input" type="number" min="1" value={form.table_count} onChange={(e) => setForm({ ...form, table_count: e.target.value })} className="mt-1.5 w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-orange-500" />
        </div>
        <button data-testid="save-branding-button" type="submit" disabled={saving} className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 disabled:opacity-60 rounded-xl px-6 py-3 font-semibold transition-colors">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes
        </button>
      </form>
    </div>
  );
};
